import { GRID_CONFIG, sortByAuditUpdatedOn } from "../../config";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const ConversationsDashboard = () => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [conversationType, setConversationType] = useState("all");
    const [selectedRow, setSelectedRow] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/conversations`, {
            credentials: "include",
        })
            .then((response) => {
                if(!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setConversations(sortByAuditUpdatedOn(data));
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching conversations:", error);
                setLoading(false);
            });
    }, []);

    const filteredConversations = Array.isArray(conversations)
        ? conversations.filter((conv) => {
              // Text search
              const matchesSearch =
                  conv.user1.toLowerCase().includes(search.toLowerCase()) ||
                  conv.user2.toLowerCase().includes(search.toLowerCase()) ||
                  conv.user1_role
                      .toLowerCase()
                      .includes(search.toLowerCase()) ||
                  conv.user2_role.toLowerCase().includes(search.toLowerCase());
              // Conversation type filter
              if(conversationType === "all") return matchesSearch;
              if(conversationType === "investor-entrepreneur") {
                  // One is investor, one is entrepreneur
                  return (
                      matchesSearch &&
                      ((conv.user1_role === "investor" &&
                          conv.user2_role === "entrepreneur") ||
                          (conv.user1_role === "entrepreneur" &&
                              conv.user2_role === "investor"))
                  );
              }
              if(conversationType === "admin-user") {
                  // One is admin, other is investor or entrepreneur
                  return (
                      matchesSearch &&
                      ((conv.user1_role === "admin" &&
                          (conv.user2_role === "investor" ||
                              conv.user2_role === "entrepreneur")) ||
                          (conv.user2_role === "admin" &&
                              (conv.user1_role === "investor" ||
                                  conv.user1_role === "entrepreneur")))
                  );
              }
              return false;
          })
        : [];

    const handleRowClick = (row) => {
        setSelectedRow(row);
        navigate(`/chat-history/${row.user1_id}/${row.user2_id}`);
    };

    const totalPages = Math.ceil(filteredConversations.length / perPage);
    const paginatedData = filteredConversations.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    // const fromRecord = (currentPage - 1) * perPage + 1;
    // const toRecord = Math.min(
    //     currentPage * perPage,
    //     filteredConversations.length
    // );
    const totalRecords = filteredConversations.length;

    const handleExportConversationsCSV = () => {
        const rows = filteredConversations.map((c) => ({
            ParticipantA: c.user1 || "",
            RoleA: c.user1_role || "",
            ParticipantB: c.user2 || "",
            RoleB: c.user2_role || "",
            LatestMessage: c.latest_message ? new Date(c.latest_message).toLocaleString() : "",
            UnpublishedCount: c.unpublished_count_entrepreneur ?? 0,
        }));

        if (!rows.length) {
            window.alert("No data to export");
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "conversations_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "50px",
                "&:hover": {
                    backgroundColor: "#E9F4F9",
                    borderLeft: "2px solid var(--navy)",
                    transition: "all 0.2s ease",
                },
            },
            selectedHighlightStyle: {
                backgroundColor: "#e6f7ff",
                borderLeft: "4px solid var(--navy)",
                color: "var(--navy)",
                fontWeight: "500",
                boxShadow: "0 0 5px rgb(var(--navy-rgb) / 0.2)",
                transition: "all 0.2s ease",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    // Helper to get role badge
    const getRoleBadge = (role) => {
        let color = "#888";
        let label = "?";
        if(role === "investor") {
            color = "var(--navy)";
            label = "I";
        } else if(role === "entrepreneur") {
            color = "#C2D7E2";
            label = "E";
        } else if(role === "admin") {
            color = "#FACC15";
            label = "A";
        }
        return (
            <span
                style={{
                    display: "inline-block",
                    minWidth: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: color,
                    color: role === "entrepreneur" ? "var(--navy)" : "white",
                    fontWeight: "bold",
                    textAlign: "center",
                    lineHeight: "22px",
                    marginRight: 8,
                    fontSize: 14,
                    border:
                        role === "entrepreneur"
                            ? "1.5px solid var(--navy)"
                            : "none",
                }}
            >
                {label}
            </span>
        );
    };

    const columns = [
        {
            name: "Participant A",
            selector: (row) => (
                <span style={{ display: "flex", alignItems: "center" }}>
                    {getRoleBadge(row.user1_role)} {row.user1}
                </span>
            ),
            sortable: true,
            width: "35%",
        },
        {
            name: "Participant B",
            selector: (row) => (
                <span style={{ display: "flex", alignItems: "center" }}>
                    {getRoleBadge(row.user2_role)} {row.user2}
                </span>
            ),
            sortable: true,
            width: "35%",
        },
        // Unpublished column - temporarily hidden but functionality preserved
        {
            name: "Unpublished",
            selector: (row) => {
                // If this is an admin-user conversation, show a minus symbol without a circle
                const isAdminUser =
                    (row.user1_role === "admin" &&
                        (row.user2_role === "investor" ||
                            row.user2_role === "entrepreneur")) ||
                    (row.user2_role === "admin" &&
                        (row.user1_role === "investor" ||
                            row.user1_role === "entrepreneur"));

                // Check if users have the same role (investor-investor, entrepreneur-entrepreneur, admin-admin)
                const isSameRoleConversation =
                    row.user1_role === row.user2_role;

                // Show dash for admin-user conversations OR same role conversations
                if(isAdminUser || isSameRoleConversation) {
                    return (
                        <span
                            title="Not applicable for publishing"
                            style={{
                                color: "var(--navy)",
                                fontWeight: "bold",
                                fontSize: "18px",
                                display: "inline-block",
                                width: 22,
                                textAlign: "center",
                            }}
                        >
                            -
                        </span>
                    );
                }

                // Normal investor-entrepreneur conversations
                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 22,
                            height: 22,
                            borderRadius: "50%",
                            background:
                                row.unpublished_count_entrepreneur > 0
                                    ? "var(--navy)"
                                    : "#C2D7E2",
                            color:
                                row.unpublished_count_entrepreneur > 0
                                    ? "white"
                                    : "var(--navy)",
                            fontWeight: "bold",
                            fontSize: "14px",
                            border:
                                row.unpublished_count_entrepreneur > 0
                                    ? "2px solid var(--navy)"
                                    : "2px solid #C2D7E2",
                            margin: "0 auto",
                            boxShadow:
                                row.unpublished_count_entrepreneur > 0
                                    ? "0 2px 8px rgb(var(--navy-rgb) / 13%)"
                                    : "none",
                            transition: "all 0.2s",
                            textAlign: "center",
                            lineHeight: "22px",
                        }}
                    >
                        {row.unpublished_count_entrepreneur > 0
                            ? row.unpublished_count_entrepreneur
                            : "0"}
                    </span>
                );
            },
            sortable: true,
            width: "15%",
            center: true,
            omit: true,
        },
        {
            name: "Latest Message",
            selector: (row) =>
                new Date(row.latest_message).toLocaleDateString(),
            sortable: true,
            width: "30%",
            right: true,
        },
    ];

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing{" "}
                {totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1} to{" "}
                {Math.min(currentPage * perPage, totalRecords)} of{" "}
                {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                CONVERSATIONS
            </h2>
            <div className="aqi-grid-toolbar">
                {/* Rows per page */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* Conversation Type Filter */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                            minWidth: "250px",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={conversationType}
                        onChange={(e) => {
                            setConversationType(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Types</option>
                        <option value="investor-entrepreneur">
                            Investor & Entrepreneur
                        </option>
                        <option value="admin-user">
                            Admin & Investor/Entrepreneur
                        </option>
                    </select>
                </div>

                <div className="aqi-grid-search">
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search users or roles..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportConversationsCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                onRowClicked={handleRowClick}
                progressPending={loading}
                pagination={false}
                highlightOnHover
                pointerOnHover
                responsive
                striped
                fixedHeader
                fixedHeaderScrollHeight="550px"
                conditionalRowStyles={[
                    {
                        when: (row) =>
                            selectedRow &&
                            row.user1_id === selectedRow.user1_id &&
                            row.user2_id === selectedRow.user2_id,
                        style: {
                            backgroundColor: "#e6f7ff",
                            borderLeft: "4px solid var(--navy)",
                            color: "var(--navy)",
                            fontWeight: "500",
                            boxShadow: "0 0 5px rgb(var(--navy-rgb) / 0.2)",
                            transform: "translateX(2px)",
                            transition: "all 0.3s ease",
                        },
                    },
                ]}
                hover
                highlightOnHoverStyle={{
                    backgroundColor: "#E9F4F9",
                    borderLeft: "2px solid var(--navy)",
                    transition: "all 0.2s ease",
                }}
            />

            {/* Custom Pagination UI */}
            <CustomPagination />
        </div>
    );
};

export default ConversationsDashboard;